/*
  Size/brand mega-menu -- desktop rail+flyout (SHOP ALL dropdown content
  in snippets/header-desktop-nav.liquid, which also hosts the plain-text
  Brands pane -- there is no standalone top-level BRANDS dropdown
  anymore, it was removed), and the mobile drawer's tap accordion
  (snippets/slide-nav.liquid). Size rows are rendered client-side from
  assets/eva-size-availability.json (window.EvaSizeAvailabilityUrl,
  wired in layout/theme.liquid same as EvaBoltyLinesUrl/
  EvaKallareFilmsManifestUrl) -- Liquid has no generic way to parse an
  arbitrary asset JSON file server-side, so this can't be rendered ahead
  of time the way a linklist-driven dropdown is; the panel shell/rail/
  headings are static Liquid, only the size rows themselves are filled
  in here, from a single fetch shared by every [data-size-list]
  container on the page (desktop flyout AND mobile accordion at once).

  This file owns: manifest fetch/caching, plain-text row rendering into
  every [data-size-list] container found anywhere in the DOM, the
  desktop hover/focus/Esc/exclusive-panel behavior for the enhanced
  <details> dropdown, the desktop rail's hover/click/focus pane-swap
  behavior, and the mobile accordion's tap-to-open/tap-to-close-in-place
  behavior. The mobile drawer's own tier-sliding mechanism (theme.js
  theme.slideNav, .js-toggle-submenu) is untouched -- Trucks and every
  other plain link still gets that for free; the accordion here is a
  separate, parallel mechanism scoped to [data-mega-accordion-*] only,
  since the tier system has no second-tap-closes concept.
*/
(function () {
  window.EVA_MEGA_MENU_ENABLED = (window.EVA_MEGA_MENU_ENABLED !== false);
  if (!window.EVA_MEGA_MENU_ENABLED) { return; }

  function ready(fn) {
    if (document.readyState !== 'loading') { fn(); }
    else { document.addEventListener('DOMContentLoaded', fn); }
  }

  // --- Manifest (assets/eva-size-availability.json) -----------------------

  var manifestData = null;
  var manifestLoadAttempted = false;

  function loadManifest(cb) {
    if (manifestData) { cb(manifestData); return; }
    if (manifestLoadAttempted) { cb(null); return; }
    manifestLoadAttempted = true;
    var url = window.EvaSizeAvailabilityUrl;
    if (!url) { cb(null); return; } // no URL wired -- fail silent
    fetch(url).then(function (r) {
      if (!r.ok) { throw new Error('bad status'); }
      return r.json();
    }).then(function (data) {
      manifestData = data;
      cb(data);
    }).catch(function () {
      manifestLoadAttempted = false; // allow a retry on the next call (e.g. after a PJAX nav)
      cb(null);
    });
  }

  // --- Current collection (2026-08-05, context-awareness) -----------------
  // The mega-menu renders from the global header on every page -- it has no
  // Liquid `collection` object of its own, so "which collection page is the
  // visitor on right now" has to be tracked in JS. layout/theme.liquid seeds
  // window.EvaCurrentCollectionHandle for the hard-load case (empty string
  // outside a collection template); every PJAX nav after that re-derives it
  // from the destination URL instead, since Liquid never re-runs on a swap
  // and the seeded global would otherwise go stale the moment a visitor
  // navigates anywhere.
  var currentCollectionHandle = window.EvaCurrentCollectionHandle || '';

  function collectionHandleFromUrl(url) {
    var m = /\/collections\/([^\/?#]+)/.exec(url || '');
    return m ? m[1] : '';
  }

  // Per-collection sizes (assets/eva-size-availability.json's `collections`
  // section, see size-menu/generate-size-availability.js) only ever exist
  // for a curated set of nav-reachable collections -- any other handle
  // (including "" outside a collection page, and "all", which is always
  // identical to the global data by definition -- see the generator's own
  // comment) simply has no entry, and this falls through to the plain
  // global `categories[key]` a visitor would see anywhere else. Never
  // guesses or partially-applies: a collection with no override for THIS
  // specific category (e.g. a wheels-only brand opening the Apparel pane)
  // falls back to global for that category alone, per-key, not all-or-
  // nothing per collection.
  function categoryFor(data, key) {
    var perCollection = currentCollectionHandle && data.collections && data.collections[currentCollectionHandle];
    if (perCollection && perCollection[key]) { return perCollection[key]; }
    return data.categories && data.categories[key];
  }

  // Static data-target-url (baked into the Liquid markup, mostly
  // /collections/all) unless a per-collection override is actually in
  // effect for this exact category, in which case the row must filter the
  // CURRENT collection instead -- "jump to the global one" is exactly the
  // bug this whole feature exists to fix, so a scoped row can never point
  // at the unscoped destination.
  function targetUrlFor(data, key, el) {
    var perCollection = currentCollectionHandle && data.collections && data.collections[currentCollectionHandle];
    if (perCollection && perCollection[key]) { return '/collections/' + currentCollectionHandle; }
    return el.getAttribute('data-target-url') || '/collections/all';
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // Verified filter URL format (phase 1, re-verified live before this
  // redo): filter.v.option.size=<value>, combined with
  // filter.v.availability=1 so a tapped row shows buyable products
  // first. encodeURIComponent alone satisfies every confirmed encoding
  // case at once -- spaces become %20 ("Youth S" -> "Youth%20S"), while
  // ".", digits and letters are left alone (decimals like "8.25" and the
  // "mm" suffix on "52mm" both need to pass through literally, and both
  // already do under encodeURIComponent's own unreserved-character set
  // with no special-casing required).
  function rowHref(targetUrl, value) {
    return targetUrl + '?filter.v.option.size=' + encodeURIComponent(value) + '&filter.v.availability=1';
  }

  // Plain-text rows, not chips -- no border/box/pill (Per's explicit
  // redo spec, supersedes the v1 chip build). Out-of-stock values render
  // as a dead <span>, struck through, never a link/focusable (current
  // live examples: decks 8.875, apparel 2XL).
  function buildSizeRowsHTML(sizes, targetUrl) {
    return sizes.map(function (s) {
      var label = escapeHtml(s.value);
      if (s.inStock) {
        return '<li><a class="mega-menu__row" href="' + rowHref(targetUrl, s.value) + '">' + label + '</a></li>';
      }
      return '<li><span class="mega-menu__row mega-menu__row--dead" aria-disabled="true">' + label + '</span></li>';
    }).join('');
  }

  // Renders into EVERY matching container on the page at once (desktop
  // flyout + mobile accordion share the same [data-size-list="<category>"]
  // convention), so one fetch covers both surfaces. Re-renders a container
  // whenever the CONTENT it would show has actually changed -- tracked by
  // which collection handle it was last rendered for (not a plain rendered/
  // not-rendered flag, per the pre-context-awareness version of this file)
  // -- so navigating from one collection to another (or to/from a
  // non-collection page) correctly refreshes rows already on the page,
  // while a same-collection PJAX-triggered re-init (or a second call with
  // nothing changed) stays a cheap no-op like it always was.
  function renderSizeLists() {
    var containers = document.querySelectorAll('[data-size-list]');
    if (!containers.length) { return; }
    loadManifest(function (data) {
      if (!data || !data.categories) { return; }
      containers.forEach(function (el) {
        if (el.__evaSizeListRenderedFor === currentCollectionHandle) { return; }
        var key = el.getAttribute('data-size-list');
        var category = categoryFor(data, key);
        if (!category || !category.sizes || !category.sizes.length) { return; }
        var targetUrl = targetUrlFor(data, key, el);
        el.innerHTML = buildSizeRowsHTML(category.sizes, targetUrl);
        el.__evaSizeListRenderedFor = currentCollectionHandle;
      });
    });
  }

  // --- Desktop panel interaction (SHOP ALL dropdown only) -----------------
  // hover-open is gated to genuine fine-pointer devices (same convention
  // as assets/bolty.js's own eye-tracking gate) -- a touch device at
  // desktop width still gets a working panel via the native <details>
  // click-toggle plus the click handler below, just without the extra
  // hover-open behavior a mouse user gets.

  var hasFinePointer = false;
  try { hasFinePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches; } catch (e) {}

  // See the Esc keydown handler below for why this exists.
  var suppressNextFocusOpen = false;

  // Matched by the summary's own visible text rather than a dedicated
  // class -- header-desktop-nav.liquid renders every dropdown item
  // through one shared loop with no per-item hook to add a class from,
  // and the nav's own link title ("SHOP ALL") is stable, deliberate
  // copy, not content that changes casually. There is no standalone
  // BRANDS nav item anymore (removed -- brands are reached only through
  // this rail's own Brands pane), so only one title is enhanced now.
  var ENHANCED_TITLES = ['SHOP ALL'];

  function isEnhancedSummary(summary) {
    var text = summary.textContent.replace(/\s+/g, ' ').trim();
    return ENHANCED_TITLES.some(function (t) { return text.indexOf(t) === 0; });
  }

  function headerDetailsList() {
    return document.querySelectorAll('[data-section-type="header-section"] .site-nav__details');
  }

  function closeDetails(details) {
    details.removeAttribute('open');
    var summary = details.querySelector(':scope > summary');
    if (summary) { summary.setAttribute('aria-expanded', 'false'); }
  }

  // Closes every header dropdown except (optionally) one -- shared by
  // both the enhanced panels below AND the plain native ones (EVA, NEW),
  // so opening any one header menu always closes every other, matching
  // "one panel open at a time" for the whole nav, not just the two new
  // panels.
  function closeAllHeaderDetails(except) {
    headerDetailsList().forEach(function (d) {
      if (d === except) { return; }
      closeDetails(d);
    });
  }

  function openDetails(details) {
    closeAllHeaderDetails(details);
    details.setAttribute('open', '');
    var summary = details.querySelector(':scope > summary');
    if (summary) { summary.setAttribute('aria-expanded', 'true'); }
  }

  function initDesktopPanels() {
    headerDetailsList().forEach(function (details) {
      var summary = details.querySelector(':scope > summary');
      if (!summary || !isEnhancedSummary(summary)) { return; }
      if (details.__evaMegaMenuBound) { return; }
      details.__evaMegaMenuBound = true;

      // First click (closed -> open): never navigates, just opens --
      // overrides the native <details> toggle so a second click can mean
      // something different (navigate) instead of just closing again.
      // Second click (already open): navigates to the item's own real
      // destination (summary's existing data-link attribute -- "SHOP
      // ALL" -> /collections/all -- already present in the markup for
      // every summary regardless of hover mode, previously only wired
      // up in hover mode; reused here as-is).
      summary.addEventListener('click', function (e) {
        e.preventDefault();
        if (details.hasAttribute('open')) {
          var link = summary.getAttribute('data-link');
          if (link) { window.location.href = link; }
        } else {
          openDetails(details);
        }
      });

      if (hasFinePointer) {
        details.addEventListener('mouseenter', function () { openDetails(details); });
        details.addEventListener('mouseleave', function () {
          if (!details.matches(':focus-within')) { closeDetails(details); }
        });
      }

      details.addEventListener('focusin', function () {
        if (suppressNextFocusOpen) { suppressNextFocusOpen = false; return; }
        if (!details.hasAttribute('open')) { openDetails(details); }
      });
      details.addEventListener('focusout', function () {
        // Deferred a tick so the newly-focused element (could be another
        // child of this same details) is registered before :focus-within
        // is checked -- otherwise a focus move between two children
        // inside the panel reads as "focus left" for one tick and closes
        // the panel out from under the visitor.
        setTimeout(function () {
          if (details.hasAttribute('open') && !details.matches(':focus-within')) { closeDetails(details); }
        }, 0);
      });
    });
  }

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') { return; }
    headerDetailsList().forEach(function (d) {
      if (!d.hasAttribute('open')) { return; }
      var summary = d.querySelector(':scope > summary');
      closeDetails(d);
      if (summary) {
        // Returning focus to the trigger (standard disclosure-widget
        // a11y) moves focus INTO this same details, which would
        // otherwise fire the focusin handler above and reopen the panel
        // immediately after Esc just closed it -- suppressed for exactly
        // the one focusin this focus() call itself triggers.
        suppressNextFocusOpen = true;
        summary.focus();
      }
    });
  });

  document.addEventListener('click', function (e) {
    headerDetailsList().forEach(function (d) {
      if (d.hasAttribute('open') && !d.contains(e.target)) { closeDetails(d); }
    });
  });

  // --- Desktop rail (SHOP ALL panel: Skateboards/Apparel/Wheels/Brands) --
  // Hover-to-switch on fine-pointer devices, click/keyboard everywhere
  // (so a touch-at-desktop-width visitor and keyboard users both get a
  // working panel). Trucks has no [data-rail-item] -- it's a plain link
  // and never touches pane state, per spec ("no flyout").
  //
  // Skateboards/Apparel/Wheels are real <a href> now (2026-08-02) --
  // Brands stays a plain, non-navigating <li> (no href to navigate to).
  // Click: on a fine pointer (desktop), never preventDefault -- the
  // click both opens/updates the pane (activate(), redundant with hover
  // but harmless right before the page unloads) AND lets the browser
  // follow the real href, satisfying "click navigates" without a
  // separate code path for middle-click/cmd-click/open-in-new-tab (those
  // are native <a> behavior, untouched). On a coarse pointer (touch),
  // ALWAYS preventDefault -- every tap opens the pane, never navigates;
  // Brands' preventDefault() here is simply a no-op (no href to cancel).
  // Enter: deliberately NOT intercepted for a real <a> -- browsers fire
  // a native click for Enter on a focused link, which the click handler
  // above already handles correctly (desktop: navigates; a hypothetical
  // keyboard+touchscreen combo: doesn't, consistent with the tap rule).
  // Only Brands' <li> (no native Enter behavior of its own) still needs
  // Enter intercepted here to open the pane via keyboard.

  function setActiveRailItem(menu, key) {
    menu.querySelectorAll('[data-rail-item]').forEach(function (item) {
      item.classList.toggle('is-active', item.getAttribute('data-rail-item') === key);
    });
    menu.querySelectorAll('[data-flyout-pane]').forEach(function (pane) {
      pane.hidden = (pane.getAttribute('data-flyout-pane') !== key);
    });
  }

  function initRailPanels() {
    document.querySelectorAll('[data-mega-menu="shop"]').forEach(function (menu) {
      if (menu.__evaRailBound) { return; }
      menu.__evaRailBound = true;
      menu.querySelectorAll('[data-rail-item]').forEach(function (item) {
        var key = item.getAttribute('data-rail-item');
        var activate = function () { setActiveRailItem(menu, key); };
        var isLink = item.tagName === 'A';
        if (hasFinePointer) { item.addEventListener('mouseenter', activate); }
        item.addEventListener('click', function (e) {
          if (!hasFinePointer) { e.preventDefault(); }
          activate();
        });
        item.addEventListener('focus', activate);
        item.addEventListener('keydown', function (e) {
          if (e.key === ' ') { e.preventDefault(); activate(); return; }
          if (e.key === 'Enter' && !isLink) { e.preventDefault(); activate(); }
        });
      });
    });
  }

  // Resets every rail panel back to its default (first/Skateboards) pane
  // on PJAX nav, so a visitor who left BRANDS open on one page doesn't
  // land back on it after navigating away and back.
  function resetRailPanels() {
    document.querySelectorAll('[data-mega-menu="shop"]').forEach(function (menu) {
      var first = menu.querySelector('[data-rail-item]');
      if (first) { setActiveRailItem(menu, first.getAttribute('data-rail-item')); }
    });
  }

  // --- Mobile accordion (slide-nav SHOP ALL: Skateboards/Apparel/Wheels/
  // Brands) -- first tap opens in place and never navigates, a second
  // tap on an already-open one closes it, opening one closes any other
  // open one. Independent of the drawer's own tier-slide system.

  function closeAllAccordionPanels() {
    document.querySelectorAll('[data-mega-accordion-panel]').forEach(function (panel) {
      panel.hidden = true;
      var key = panel.getAttribute('data-mega-accordion-panel');
      var trigger = document.querySelector('[data-mega-accordion-trigger="' + key + '"]');
      if (trigger) { trigger.setAttribute('aria-expanded', 'false'); }
    });
  }

  function initMobileAccordions() {
    document.querySelectorAll('[data-mega-accordion-trigger]').forEach(function (trigger) {
      if (trigger.__evaAccordionBound) { return; }
      trigger.__evaAccordionBound = true;
      trigger.addEventListener('click', function () {
        var key = trigger.getAttribute('data-mega-accordion-trigger');
        var panel = document.querySelector('[data-mega-accordion-panel="' + key + '"]');
        if (!panel) { return; }
        var wasOpen = !panel.hidden;
        closeAllAccordionPanels();
        panel.hidden = wasOpen;
        trigger.setAttribute('aria-expanded', panel.hidden ? 'false' : 'true');
      });
    });
  }

  function init() {
    renderSizeLists();
    initDesktopPanels();
    initRailPanels();
    initMobileAccordions();
  }

  ready(init);

  // The header/drawer live outside #MainContent (same reasoning as
  // Bolty/Eva Radio), so PJAX content swaps never touch or recreate
  // them -- init() re-running here is mostly a defensive no-op in
  // practice (initRailPanels/initMobileAccordions skip already-bound
  // elements), except for the three explicit state changes below: every
  // open header dropdown closes (matching theme.js's own
  // HeaderSection.hoverMenu behavior on this same event), every rail
  // panel returns to its default pane, every open mobile accordion panel
  // closes -- a stale open panel on the destination page would be wrong,
  // same as it would for the theme's own native dropdowns -- and
  // currentCollectionHandle is re-derived from the destination URL
  // (e.detail.url, set by eva-pjax.js's applySwap()) BEFORE init() runs,
  // so renderSizeLists() picks up the new page's own per-collection sizes
  // (or correctly falls back to global off a collection page) instead of
  // reusing whatever collection the visitor was just on. Parsed from the
  // URL rather than trusted from window.EvaCurrentCollectionHandle past
  // the first paint -- that global is seeded by Liquid, which never
  // re-runs on a PJAX swap, so it would silently go stale on the very
  // first navigation otherwise.
  document.addEventListener('eva:pjax:loaded', function (e) {
    currentCollectionHandle = collectionHandleFromUrl(e.detail && e.detail.url);
    closeAllHeaderDetails(null);
    resetRailPanels();
    closeAllAccordionPanels();
    init();
  });
})();
